import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { arrayMoveImmutable } from "array-move";
import { connect } from "react-redux";
// services
import * as Api from "../../../api";
// components
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import AlertMsg from "../../../components/AlertMsg";
import RestaurantHeader from "../../../components/RestaurantHeader";
import AppDialog from "../../../components/AppDialog";
import CourseAddForm from "./CourseAddForm";
import CourseList from "./CourseList";
// constants
import { FOOTER_XPADDING, COURSE_TYPE_LIST } from "../../../constants";
import { THEME_MODE } from "../../../constants/Theme";
// assets
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete3.svg";

const CourseTypeList = COURSE_TYPE_LIST;

const Course = (props) => {
  const restaurantId = useParams().rest_id;
  const theme_mode = THEME_MODE[props.themeMode];
  const navigate = useNavigate();

  // State declarations
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState("");
  const [courseType, setCourseType] = useState("");
  const [courseCategories, setCourseCategories] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [editCourse, setEditCourse] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [error, setError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });
  const [appDialogState, setAppDialogState] = useState({ open: false });

  useEffect(() => {
    getCategoryList();
  }, []);

  const getCategoryList = () => {
    setIsLoading(true);
    const filter = "all";
    Api.getCategoryList(restaurantId, filter).then((response) => {
      if (response.success) {
        const data = response.data.rows.sort((a, b) => a?.order - b?.order);
        setCategoryList(data);
        getCourseList(1, data);
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        setIsLoading(false);
      }
    });
  };

  const getCourseList = (num, category_list) => {
    setIsLoading(true);
    const filter = "/all";

    // Create a lookup map for categories by ID for O(1) access
    const categoryMap = {};
    category_list.forEach((category) => {
      categoryMap[category._id] = category;
    });

    Api.getCourseList(restaurantId, filter).then((response) => {
      if (response.success) {
        const data = response.data.rows.sort((a, b) => a?.order - b?.order);

        const list = [];
        data.forEach((course) => {
          // Use the lookup map to directly find matching categories
          const cat_list = course?._idRestaurantCategory.length
            ? course._idRestaurantCategory
                .map((catId) => categoryMap[catId])
                .filter(Boolean)
            : [];

          list.push({ ...course, _idRestaurantCategory: cat_list });
        });

        setCourseList(list);
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
      setIsLoading(false);
    });
  };

  const onEditClick = (x) => {
    setEditError(null);
    setEditCourse(x);
    if (document.getElementById("courseId" + x._id)) {
      setTimeout(() => {
        document
          .getElementById("courseId" + x._id)
          .getElementsByTagName("input")[0]
          .focus();
      }, 100);
    }
  };

  const onDeleteClick = (val) => {
    setAppDialogState({
      open: true,
      title: (
        <Box>
          <Typography sx={{ margin: 0 }}>
            <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
          >
            Delete Course?
          </Typography>
        </Box>
      ),
      content: <Box>Are you sure you want to delete course: {val?.name}?</Box>,
      firstBtnText: "Cancel",
      secondBtnText: "Delete",
      onClose: onCloseConfirmDelete,
      onConfirm: () => confirmDeleteMenu(val._id),
    });
  };

  const confirmDeleteMenu = (id) => {
    setBtnLoading(true);
    Api.deleteCourse(restaurantId, id).then((response) => {
      if (response.success) {
        setMsgAlert({
          open: true,
          message: "Deleted Successfully",
          msgType: "success",
        });
        let list = [...courseList];
        setCourseList(list.filter((x) => x._id != id));
        setAppDialogState({ open: false });
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
      setBtnLoading(false);
    });
  };

  const onAddCourse = () => {
    let err = null;
    if (course.trim() == "") {
      err = { course: "Course is required" };
    }

    if (courseCategories.length == 0) {
      err = { ...err, catgeories: "Catgeories is required" };
    }

    setError(err);
    if (err != null) {
      return;
    }

    addCourse({
      name: course,
      courseType: courseType,
      _idRestaurantCategory: courseCategories,
    });
  };

  const onUpdateCourse = () => {
    let err = null;
    if (editCourse.name.trim() == "") {
      err = { course: "Course is required" };
    }

    if (editCourse._idRestaurantCategory.length == 0) {
      err = { ...err, catgeories: "Catgeories is required" };
    }

    setEditError(err);
    if (err != null) {
      return;
    }
    addCourse(editCourse);
  };

  const addCourse = (data) => {
    setIsLoading(true);
    const coursePayload = {
      _idRestaurant: restaurantId,
      name: data.name,
      courseType: data.courseType,
      _idRestaurantCategory: data._idRestaurantCategory.map((x) => x._id),
    };

    Api.addUpdateCourse(
      coursePayload,
      restaurantId,
      data?._id ? data._id : null
    ).then((response) => {
      if (response.success) {
        setCourse("");
        setCourseType("");
        setCourseCategories([]);
        const list = [...courseList];
        if (data?._id) {
          const findIndex = list.findIndex((x) => x._id == editCourse._id);
          if (findIndex >= 0) {
            list[findIndex] = {
              ...list[findIndex],
              ...coursePayload,
              _idRestaurantCategory: editCourse._idRestaurantCategory,
            };
            setEditCourse(null);
          }
        } else {
          list.push({
            ...response.data,
            _idRestaurantCategory: data._idRestaurantCategory,
          });
        }
        setCourseList(list);
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: "error" });
      }
      setIsLoading(false);
    });
  };

  const onCancelCourse = () => {
    setEditCourse(null);
  };

  const onSetCourse = (val) => {
    const data = { ...editCourse };
    data.name = val;
    setEditCourse(data);
  };

  const onSetCourseType = (val) => {
    const data = { ...editCourse };
    data.courseType = val;
    setEditCourse(data);
  };

  const onSetCourseCategories = (val) => {
    const data = { ...editCourse };
    data._idRestaurantCategory = val;
    setEditCourse(data);
  };

  const onCloseConfirmDelete = () => {
    setAppDialogState({ open: false });
  };

  const onSortEnd = async (oldIndex, newIndex) => {
    const list = arrayMoveImmutable(courseList, oldIndex, newIndex);
    const dataList = list.map((x, i) => {
      return { order: i + 1, _idCourse: x._id };
    });
    const data = JSON.stringify(dataList);
    const response = await Api.courseRearrange(data, restaurantId);
    if (response.success) {
      setCourseList(list);
    } else {
      setCourseList(categoryList);
      props.setMsgAlert({
        open: true,
        message: response.msg,
        msgType: "error",
      });
    }
  };

  const handleOnChaneCourseType = (val) => {
    setCourseType(val);
  };

  return (
    <Container
      page={"settings"}
      p={1.5}
      fpx={FOOTER_XPADDING[props.themeMode]}
      fpy={0}
      onSearch={() => null}
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "70vh",
          }}
        >
          <Loader />
        </Box>
      ) : (
        <Grid
          container
          item
          md={12}
          xs={12}
          p={1.5}
          style={{ backgroundColor: theme_mode.card3, borderRadius: 15 }}
        >
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Manage Courses"}
              onBackBtnClick={() =>
                navigate(`/restaurant/${restaurantId}/manage_menu`)
              }
            />
          </Grid>
          <Grid container item xs={12} mt={2} justifyContent={"space-between"}>
            <Grid
              item
              xs={12}
              md={5.2}
              p={props.themeMode == "dark" ? 2 : 0}
              mb={props.themeMode == "dark" ? 1 : 0}
              style={{ backgroundColor: theme_mode.inputBg5, borderRadius: 15 }}
            >
              <CourseAddForm
                course={course}
                setCourse={setCourse}
                courseType={courseType}
                handleOnChaneCourseType={handleOnChaneCourseType}
                courseCategories={courseCategories}
                setCourseCategories={setCourseCategories}
                onAddCourse={onAddCourse}
                categoryList={categoryList}
                theme_mode={theme_mode}
                error={error}
                CourseTypeList={CourseTypeList}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6.6}
              p={props.themeMode == "dark" ? 2 : 0}
              mb={props.themeMode == "dark" ? 1 : 0}
              style={{ backgroundColor: theme_mode.inputBg5, borderRadius: 15 }}
            >
              {courseList.length ? (
                <CourseList
                  courseList={courseList}
                  onSortEnd={onSortEnd}
                  editCourse={editCourse}
                  onEditClick={onEditClick}
                  onDeleteClick={onDeleteClick}
                  theme_mode={theme_mode}
                  onSetCourse={onSetCourse}
                  onSetCourseType={onSetCourseType}
                  onSetCourseCategories={onSetCourseCategories}
                  onUpdateCourse={onUpdateCourse}
                  onCancelCourse={onCancelCourse}
                  CourseTypeList={CourseTypeList}
                  categoryList={categoryList}
                  themeMode={props.themeMode}
                  editError={editError}
                />
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      )}
      <AppDialog
        open={appDialogState.open}
        onClose={appDialogState.onClose}
        content={appDialogState.content}
        title={appDialogState.title}
        firstBtnContent={appDialogState.firstBtnText}
        secondBtnContent={
          btnLoading ? "Please Wait..." : appDialogState.secondBtnText
        }
        firstBtnClickHandler={appDialogState.onClose}
        secondBtnClickHandler={appDialogState.onConfirm}
        options={{ btnLoading }}
      />
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() =>
          setMsgAlert({ open: false, message: "", msgType: "error" })
        }
      />
    </Container>
  );
};

const mapStateToProps = (state) => {
  return {
    themeMode: state.userData.themeMode,
    restaurantId: state.userData.restaurantId,
  };
};

export default connect(mapStateToProps, null)(Course);
